import { prepareHeaders } from "./";
import cincolabdataEndpoints from "./cincolabdata.endpoints";

import type { Event } from "./events";
import type { LocalizedField } from "./types";

export const ONLINE_EVENT_KEY =
  "FORD_MODEL_E_DIGITAL_ACTIVATION-ONLINE" as const;

export interface FieldChoice {
  value: string;
  label: LocalizedField;
}

export interface DisplayCondition {
  field_key: string;
  condition: string;
  value: string;
}

export const FieldType = {
  TEXT: "text",
  DATE: "date",
  PHONE: "phone",
  POSTAL_CODE: "postal_code",
  TEXTAREA: "textarea",
  CHECKBOX: "checkbox",
  YESNO: "yesno",
  NUMBER: "number",
  CHOICES: "choices",
  RATING: "rating",
  MARKETING: "marketing",
  PILLS: "pills",
} as const;

export interface Field {
  key: string;
  enabled: boolean;
  optional: boolean;
  hidden: boolean;
  type: (typeof FieldType)[keyof typeof FieldType];
  choices?: FieldChoice[];
  display_conditions: DisplayCondition[];
  label: LocalizedField;
  answer?: string | null;
}

export interface PublicFormInstance {
  key: string;
  title: LocalizedField;
  description: LocalizedField;
  footer: LocalizedField;
  image_url: LocalizedField;
  fields: Field[];
  event: Event;
  region: string;
  activation_id: number;
  email_field_position: number;
  submit_field_position: number;
  default_locale: string;
}

/**
 * Retrieves a form instance from the server.
 * @returns A promise that resolves to the form instance if found, or null if not found.
 */
export const getFormInstance = async (): Promise<PublicFormInstance | null> => {
  try {
    const response = await fetch(
      cincolabdataEndpoints.public.survey(
        import.meta.env.VITE_FORM_INSTANCE_KEY,
      ),
      {
        method: "GET",
        headers: prepareHeaders(),
        mode: "cors",
        redirect: "follow",
      },
    );

    if (response.status !== 200) {
      return null;
    }

    return await response.json();
  } catch {
    // Network failure (fetch rejects) or malformed JSON — surface as "not found"
    // so the caller shows the retry UI instead of throwing an unhandled rejection.
    return null;
  }
};
